import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import PasswordInput from "@/components/PasswordInput";
import ThemeToggleFloating from "@/components/ThemeToggleFloating";
import { ArrowRight } from "lucide-react";
import { ResendVerificationButton } from "@/components/ResendVerificationButton";

export default function LoginPage() {
  const { login, error, unverifiedEmail } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) {
      const next = params.get("next") || "";
      nav(next.startsWith("/") && !next.startsWith("//") ? next : "/");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="relative hidden md:block bg-foreground overflow-hidden">
        <img
          src="https://images.pexels.com/photos/32471031/pexels-photo-32471031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Stadium floodlights"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-background">
          <div className="inline-block bg-[#0b1735] p-3 border-2 border-background/20">
            <img
              src="/logo-meerjarenplan.png"
              alt="MeerjarenplanOnline.nl"
              className="h-10 w-auto"
              data-testid="brand-logo-hero"
            />
          </div>
          <div>
            <div className="font-display font-black text-5xl lg:text-7xl uppercase leading-[0.9] tracking-tighter">
              {t("Investeer")}<br/>{t("met")}<br/><span className="text-secondary">{t("Beleid.")}</span>
            </div>
            <p className="mt-6 max-w-md text-background/85 text-lg font-medium">
              {t("Modelleer langetermijninvesteringen in de faciliteiten van je club — ligplaatsen, gebouwen, banen — en vergelijk beste/basis/slechtste scenario's in enkele seconden.")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10 bg-background grid-bg relative">
        <ThemeToggleFloating className="top-4 right-4" />
        <div className="w-full max-w-md">
        <div data-testid="register-cta-box" className="bg-card border-2 border-foreground p-5 heavy-shadow mb-10 -mt-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="stat-label mb-1">{t("Nieuw hier?")}</div>
            <div className="font-display font-black text-xl uppercase tracking-tight">{t("Nog geen account?")}</div>
          </div>
          <Link
            to="/register?trial=1"
            data-testid="link-register"
            className="btn-brutal bg-secondary text-secondary-foreground px-4 py-2 font-display uppercase font-bold tracking-wider text-sm inline-flex items-center gap-2"
          >
            {t("Maak er een aan")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <form onSubmit={submit} className="w-full bg-card border-2 border-foreground p-8 heavy-shadow">
          <div className="stat-label mb-2">{t("Toegang · Dashboard")}</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-6">{t("Inloggen")}</h1>

          <label className="stat-label">{t("E-mail")}</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="w-full mt-1 mb-4 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <label className="stat-label">{t("Wachtwoord")}</label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            testId="login-password"
            className="mt-1 mb-4"
          />

          {error && (
            <div data-testid="login-error" className="border-2 border-destructive bg-destructive/10 text-destructive p-3 text-sm mb-4">{error}</div>
          )}
          {unverifiedEmail && (
            <div className="mb-4" data-testid="login-unverified">
              <ResendVerificationButton email={unverifiedEmail} compact />
            </div>
          )}

          <button
            disabled={busy}
            data-testid="login-submit"
            className="btn-brutal w-full bg-primary text-primary-foreground py-3 font-display uppercase font-black tracking-wider text-lg inline-flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {busy ? t("Bezig met inloggen…") : (<>{t("Inloggen")} <ArrowRight className="w-5 h-5" /></>)}
          </button>

          <p className="mt-5 text-sm">
            <Link to="/forgot-password" data-testid="link-forgot-password" className="font-bold underline underline-offset-2 text-muted-foreground hover:text-primary">
              {t("Wachtwoord vergeten?")}
            </Link>
          </p>
        </form>
        </div>
      </div>
    </div>
  );
}
